import { useState, useEffect, useRef, useCallback } from 'react';

interface UseQuizTimerProps {
  timeLimit: number; // Time limit in seconds
  onTimeout: () => void; // Callback when time runs out
  isActive: boolean; // Whether the timer should be running
  onTimeUpdate?: (timeLeft: number) => void; // Optional callback for time updates
}

export const useQuizTimer = ({ timeLimit, onTimeout, isActive, onTimeUpdate }: UseQuizTimerProps) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(isActive);
  const onTimeoutRef = useRef(onTimeout);
  const onTimeUpdateRef = useRef(onTimeUpdate);

  // Update the refs when callbacks change
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setTimeLeft(timeLimit);
    
    timerRef.current = setInterval(() => {
      setTimeLeft((prevTime) => {
        const newTime = prevTime - 1;
        
        if (onTimeUpdateRef.current) {
          onTimeUpdateRef.current(newTime);
        }
        
        if (newTime <= 0) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          onTimeoutRef.current();
          return 0;
        }
        
        return newTime;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetTimer = () => {
    stopTimer();
    setTimeLeft(timeLimit);
    // Start timer immediately if active
    if (isActiveRef.current && timeLimit > 0 && !timerRef.current) {
      startTimer();
    }
  };

  const pauseTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const addTime = useCallback((seconds: number) => {
    if (seconds <= 0) return;
    setTimeLeft((prev) => prev + seconds);
  }, []);

  const resumeTimer = () => {
    if (isActive && timeLeft > 0 && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          const newTime = prevTime - 1;
          
          if (onTimeUpdateRef.current) {
            onTimeUpdateRef.current(newTime);
          }
          
          if (newTime <= 0) {
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
            onTimeoutRef.current();
            return 0;
          }
          
          return newTime;
        });
      }, 1000);
    }
  };

  useEffect(() => {
    if (isActive && !timerRef.current) {
      startTimer();
    } else if (!isActive) {
      stopTimer();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive]);

  useEffect(() => {
    setTimeLeft(timeLimit);
  }, [timeLimit]);

  return {
    timeLeft,
    startTimer,
    stopTimer,
    resetTimer,
    pauseTimer,
    resumeTimer,
    addTime,
    isRunning: timerRef.current !== null
  };
}; 